// Logical Groups tab. Lists groups + create modal; clicking a row selects
// it inline and reveals its members below. Members reference areas, so we
// load the site's areas once here and share with the members panel.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { logicalGroups } from "../../api/endpoints/logicalGroups";
import { adminQueryOptions } from "../../api/adminQueryOptions";
import { qk } from "../../api/queryKeys";
import { EmptyState } from "../../components/ui/EmptyState";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { Modal } from "../../components/ui/Modal";
import { Pill } from "../../components/ui/Pill";
import { Table } from "../../components/ui/Table";
import { PageHeader } from "../../components/shell/PageHeader";
import type { LogicalGroupOut } from "../../api/types/logicalGroup";

import { GroupMembersPanel } from "./GroupMembersPanel";
import { LogicalGroupCreateForm } from "./LogicalGroupCreateForm";
import { useSiteAreas } from "../areas/useSiteAreas";

interface Props {
  accountSlug: string;
  tenantSlug: string;
  siteSlug: string;
}

export function LogicalGroupListPanel({
  accountSlug,
  tenantSlug,
  siteSlug,
}: Props) {
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  const q = useQuery({
    ...adminQueryOptions,
    queryKey: qk.logicalGroups(accountSlug, tenantSlug, siteSlug),
    queryFn: () =>
      logicalGroups.list(accountSlug, tenantSlug, siteSlug).then((r) => r.data),
  });

  // Shared across the members panel + member-label resolution.
  const areas = useSiteAreas(accountSlug, tenantSlug, siteSlug);

  return (
    <>
      <PageHeader
        title="Logical Groups"
        subtitle="HVAC zones, lighting scenes, and automation groups — sets of areas."
        actions={
          <button type="button" style={primaryBtn} onClick={() => setCreateOpen(true)}>
            Create group
          </button>
        }
      />

      {q.isPending && <p style={{ color: "var(--text-dim)" }}>Loading…</p>}
      {q.error && <ErrorBanner error={q.error} title="Failed to list logical groups" />}
      {q.data && (
        <Table<LogicalGroupOut>
          columns={[
            {
              key: "slug",
              header: "Slug",
              render: (g) => (
                <button
                  type="button"
                  onClick={() => setSelected(g.slug)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    color: "var(--accent)",
                    cursor: "pointer",
                    fontWeight: selected === g.slug ? 700 : 400,
                  }}
                >
                  {g.slug}
                </button>
              ),
            },
            { key: "label", header: "Label" },
            { key: "kind", header: "Kind", render: (g) => <Pill tone="info">{g.kind}</Pill> },
          ]}
          rows={q.data}
          rowKey={(g) => g.id}
          empty={
            <EmptyState
              title="No logical groups on this site"
              description="Create an HVAC zone or lighting scene, then add areas to it."
            />
          }
        />
      )}

      {selected && (
        <GroupMembersPanel
          accountSlug={accountSlug}
          tenantSlug={tenantSlug}
          siteSlug={siteSlug}
          groupSlug={selected}
          areas={areas}
        />
      )}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        closeDisabled={creating}
        title={`Create logical group on ${siteSlug}`}
        width={480}
      >
        <LogicalGroupCreateForm
          accountSlug={accountSlug}
          tenantSlug={tenantSlug}
          siteSlug={siteSlug}
          onDone={() => setCreateOpen(false)}
          onPendingChange={setCreating}
        />
      </Modal>
    </>
  );
}

const primaryBtn: React.CSSProperties = {
  background: "var(--accent)",
  color: "var(--bg)",
  border: "none",
  padding: "8px 16px",
  borderRadius: 4,
  fontSize: "0.9rem",
  fontWeight: 600,
  cursor: "pointer",
};
